/**
 * API Configuration
 *
 * This file centralizes API configuration for different environments.
 * It determines the appropriate API base URL based on the environment.
 */

// Environment configuration
const isDev = import.meta.env.DEV;
const domain = import.meta.env.VITE_DOMAIN || (isDev ? 'content-growth.cn' : 'content-growth.com');

// Determine URLs based on environment
let baseApiUrl: string;
let baseWwwUrl: string;

if (isDev) {
  // Local development
  baseApiUrl = `http://api.${domain}`;
  baseWwwUrl = `http://www.${domain}`;
} else {
  // Production / Staging
  baseApiUrl = `https://api.${domain}`;
  baseWwwUrl = `https://www.${domain}`;
}

// Export the API configuration
export const apiConfig = {
  baseUrl: baseApiUrl,
  wwwUrl: baseWwwUrl,

  // Helper function to get a full API URL
  getUrl: (path: string): string => {
    // Remove leading slash if present to avoid double slashes
    const cleanPath = path.startsWith('/') ? path.substring(1) : path;
    return `${baseApiUrl}/${cleanPath}`;
  },

  // Helper function to get a full public asset URL
  getPublicAssetsUrl: (path: string | null): string | null => {
    if (!path) return null;

    // If the URL is already absolute (starts with http), return it as is
    if (path.startsWith('http')) return path;

    // Remove leading slash if present to avoid double slashes
    const cleanPath = path.startsWith('/') ? path.substring(1) : path;

    // In development mode, use the local proxy to avoid CORS issues
    if (isDev) {
      // If the path already contains /public/assets, use it directly
      if (cleanPath.includes('public/assets')) {
        return `/${cleanPath}`;
      }
      // Otherwise, assume it's an avatar path and format accordingly
      return `/public/assets/${cleanPath}`;
    }

    // In production, use the API base URL
    return `${baseApiUrl}/${cleanPath}`;
  },

  // Common headers
  headers: {
    'Content-Type': 'application/json',
  },

  // Timeout in milliseconds
  timeout: 30000,
};

// Export environment information
export const isProduction = !isDev;
export const isDevelopment = isDev;
export const isTest = false; // Vite doesn't have a standard test flag like NODE_ENV, usually handled by test runner
